import { documents, mutateDocuments } from "@/api/documentFile/useS3Query";
import { useModal } from "@/features/modal/ModalStackManager";
import customAlert from "@/features/ui/alert/Alert";
import { Box, Button, Flex, Text } from "@mantine/core";
import {
  S3ApiS3CopyPostRequest,
  S3ListGet200ResponseInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { useMutation, useQueries } from "@tanstack/react-query";
import { useState } from "react";
import { TreeItem } from "../tree/TreeItem";

interface CopyFormProps {
  data: S3ListGet200ResponseInner[];
  selected: S3ListGet200ResponseInner[];
  refetch: () => void;
}
export const CopyForm = (params: CopyFormProps) => {
  const { data, selected, refetch } = params;
  const { closeModal } = useModal();
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());
  const [selectedTargetPath, setSelectedTargetPath] = useState<string | null>(null);
  const selectedKeys = new Set(selected.map((item) => item.Key));

  const { mutate: copyMutate } = useMutation(
    (params: S3ApiS3CopyPostRequest) => mutateDocuments.copy(params).mutationFn(undefined),
    {
      onSuccess: () => {
        customAlert("파일 복사에 성공하였습니다.", "파일 복사", "green");
        refetch();
        closeModal(null);
      },
      onError: (error: any) => {
        customAlert(
          error.response?.data?.message ?? "파일 복사에 실패하였습니다.",
          "파일 복사 실패",
          "red",
        );
      },
    },
  );

  const toggleFolder = (path: string) => {
    setOpenFolders((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  };

  const openFolderPaths = Array.from(openFolders);

  const folderQueries = useQueries({
    queries: openFolderPaths.map((prefix) => {
      const { queryKey, queryFn } = documents.get({ prefix });
      return {
        queryKey,
        queryFn,
        enabled: !!prefix,
      };
    }),
  });

  const renderTree = (
    items: S3ListGet200ResponseInner[],
    parentPath: string = "",
    depth: number = 0,
  ): React.ReactNode => {
    const currentLevelItems = items.filter((item) => {
      const relativePath = item.Key?.replace(parentPath, "");
      return relativePath?.split("/").filter(Boolean).length === 1;
    });

    return (
      <>
        {currentLevelItems
          .filter((item) => item.isDirectory)
          .map((item) => {
            const isOpen = openFolders.has(item.Key!);
            const subQuery = folderQueries.find((_, i) => openFolderPaths[i] === item.Key);
            const subItems: S3ListGet200ResponseInner[] = subQuery?.data?.data ?? [];

            return (
              <TreeItem
                key={item.Key}
                name={item.name ?? ""}
                depth={depth}
                isOpen={isOpen}
                onToggle={() => toggleFolder(item.Key!)}
                onClick={
                  selectedKeys.has(item.Key!) ? undefined : () => setSelectedTargetPath(item.Key!)
                }
                isSelected={selectedTargetPath === item.Key}
                disabled={selectedKeys.has(item.Key!)}
              >
                {isOpen && (
                  <Box>
                    {subQuery?.isLoading ? (
                      <Text size="xs" c="dimmed" pl={depth * 16 + 24}>
                        불러오는 중...
                      </Text>
                    ) : subItems.length > 0 ? (
                      renderTree(subItems, item.Key!, depth + 1)
                    ) : (
                      <Text size="xs" c="gray" pl={depth * 16 + 24}>
                        하위 항목 없음
                      </Text>
                    )}
                  </Box>
                )}
              </TreeItem>
            );
          })}
      </>
    );
  };

  return (
    <Box w="24rem">
      {renderTree(data)}
      <Flex justify={"flex-end"}>
        <Button
          mt="md"
          onClick={() =>
            copyMutate({
              s3MovePostRequest: {
                sourceKey: selected[0].Key ?? "",
                destinationKey: selectedTargetPath ?? "",
              },
            })
          }
        >
          복사
        </Button>
      </Flex>
    </Box>
  );
};
